<template>
    <section class="flex flex-col gap-2">
        <div class="flex items-center gap-2 flex-wrap">
            <span class="scaffold-details-small text-ink-muted">{{ heading }}</span>
            <BaseButton theme="neutral" size="sm" class="ml-auto" :disabled="!summary || downloading || loading" @click="onDownloadCsv">
                <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                {{ downloadLabel }}
            </BaseButton>
        </div>

        <!-- Mounted once and left in place so each window change is announced. -->
        <p class="sr-only" role="status">{{ loading ? t("reflections.admin.completion.refreshing") : "" }}</p>

        <BaseCard padding="md" class="flex flex-col gap-4">
            <!-- Inside the card so it is plain that the window scopes these numbers and nothing below. -->
            <BaseToggleGroup v-model="timeWindow" appearance="chips" :options="windowOptions" :aria-label="t('reflections.admin.window.label')" />

            <p v-if="!summary" class="body-small text-ink-muted" :class="{ 'opacity-60': loading }" :aria-busy="loading">{{ emptyText }}</p>

            <div v-else class="completion-layout" :class="{ 'opacity-60': loading }" :aria-busy="loading">
                <div class="flex flex-col gap-4 min-w-0">
                    <div class="flex items-baseline gap-2.5 flex-wrap">
                        <span class="display-medium text-ink">{{ formatNumber(summary.submitted_pct / 100, "percent") }}</span>
                        <span class="body-small text-ink-muted">{{ t("reflections.admin.completion.ofAssessmentsSubmitted") }}</span>
                        <span v-if="windowLabel" class="details text-ink-muted ml-auto">{{ windowLabel }}</span>
                    </div>

                    <div class="completion-bar">
                        <span class="completion-fill fill-submitted" :class="{ 'opacity-30': isMuted('submitted') }" :style="{ width: widthOf(summary.submitted) }"></span>
                        <span class="completion-fill fill-in-progress" :class="{ 'opacity-30': isMuted('in_progress') }" :style="{ width: widthOf(summary.in_progress) }"></span>
                    </div>

                    <BaseToggleGroup v-model="state" appearance="chips" :options="stateOptions" :aria-label="t('reflections.admin.completion.stateGroup')">
                        <template #option="{ option }">
                            <span v-if="option.key !== 'all'" class="swatch mr-2" :class="SWATCH_CLASS[option.key]" aria-hidden="true"></span>
                            {{ option.label }}
                        </template>
                    </BaseToggleGroup>

                    <p class="body-small text-ink-muted">{{ noteLine }}</p>
                </div>

                <div class="completion-tiles">
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">{{ t("reflectionsAdmin.campaign.participants") }}</span>
                        <span class="title-subsection text-ink">{{ summary.participants }}</span>
                        <span class="details text-ink-muted">{{ sourceLine }}</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.completion.started") }}</span>
                        <span class="title-subsection text-ink">{{ summary.submitted + summary.in_progress }}</span>
                        <span class="details text-ink-muted">{{
                            t("reflections.admin.completion.ofAssessments", { pct: formatNumber(summary.started_pct / 100, "percent") })
                        }}</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.completion.ratersInvited") }}</span>
                        <span class="title-subsection text-ink">{{ summary.raters_invited }}</span>
                        <span class="details text-ink-muted">{{ t("reflections.admin.completion.ratersPerAssessment", { count: summary.raters_per_assessment }) }}</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.completion.ratersResponded") }}</span>
                        <span class="title-subsection text-ink">{{ summary.raters_responded ?? "—" }}</span>
                        <span class="details text-ink-muted">{{ raterResponseLine }}</span>
                    </div>
                </div>
            </div>
        </BaseCard>

        <BaseNotification v-if="refreshError" theme="error" :text="refreshError" @dismiss="refreshError = null" />
        <BaseNotification v-if="downloadError" theme="error" :text="downloadError" @dismiss="downloadError = null" />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCompletionStateFilter, AdminCompletionSummary, AdminCompletionWindow } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToggleGroup from "~vue/components/BaseToggleGroup.vue";
import { COMPLETION_STATES, COMPLETION_WINDOWS, DEFAULT_STATE, DEFAULT_WINDOW, readAdminHubQuery, writeAdminHubQuery } from "~vue/pages/reflections/adminHubQuery";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { downloadApiFile } from "~vue/utils/downloadApiFile";
import { formatDateRange } from "~vue/utils/formatDate";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { daysFromToday, formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    // The Active window's summary, as the hub payload carries it at first paint.
    completion: AdminCompletionSummary | null;
}

const { completion } = defineProps<Props>();
// Which participants the export keeps; "all" is the unfiltered view.
const state = defineModel<AdminCompletionStateFilter>("state", { default: DEFAULT_STATE });
const { t } = useI18n();

const SWATCH_CLASS: Record<AdminCompletionStateFilter, string> = {
    all: "",
    submitted: "swatch-submitted",
    in_progress: "swatch-in-progress",
    not_started: "swatch-not-started",
};

// The window scopes this card and its export only. It lives in the URL with the
// state pill so a refreshed or shared link opens the same view.
const timeWindow = ref<AdminCompletionWindow>(readAdminHubQuery().window);
// The window the numbers on screen were loaded for; a failed refresh snaps back to it.
const appliedWindow = ref<AdminCompletionWindow>(DEFAULT_WINDOW);
const summary = ref<AdminCompletionSummary | null>(completion);
const loading = ref(false);
const refreshError = ref<string | null>(null);
const downloading = ref(false);
const downloadError = ref<string | null>(null);

let requestSequence = 0;

// The hub re-reads its payload after a campaign closes. That is the Active card;
// a dated card is re-read for its own window, since the close changed it too.
watch(
    () => completion,
    (next) => {
        if (appliedWindow.value === DEFAULT_WINDOW) {
            summary.value = next;
        } else {
            void loadWindow(appliedWindow.value);
        }
    },
);

async function loadWindow(window: AdminCompletionWindow) {
    const sequence = ++requestSequence;
    loading.value = true;
    const params = new URLSearchParams({ window });
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/completion?${params}`).json<AdminCompletionSummary | null>();
    if (sequence !== requestSequence) {
        return;
    }
    loading.value = false;
    if (fetchError.value) {
        // Keep the numbers the admin already has, and the pill that describes them.
        timeWindow.value = appliedWindow.value;
        refreshError.value = t("reflections.admin.completion.refreshFailed");
        return;
    }
    summary.value = data.value ?? null;
    appliedWindow.value = window;
    refreshError.value = null;
}

watch(timeWindow, (window) => {
    // Back on the loaded window, by a snap-back or by the admin's own click: the
    // numbers are already that window's, and any request still out for another
    // window must not land on top of them.
    if (window === appliedWindow.value) {
        requestSequence += 1;
        loading.value = false;
        return;
    }
    void loadWindow(window);
});

watch([timeWindow, state], ([window, stateFilter]) => {
    writeAdminHubQuery({ window, state: stateFilter });
});

onMounted(() => {
    // A value the URL named but the card does not know fell back to the default; the
    // address bar should say what the card shows.
    writeAdminHubQuery({ window: timeWindow.value, state: state.value });
    if (timeWindow.value !== appliedWindow.value) {
        void loadWindow(timeWindow.value);
    }
});

const windowOptions = computed(() => COMPLETION_WINDOWS.map((key) => ({ key, label: windowLabelFor(key) })));

function windowLabelFor(window: AdminCompletionWindow): string {
    switch (window) {
        case "active":
            return t("reflections.admin.window.active");
        case "last_30_days":
            return t("reflections.admin.window.last_30_days");
        case "last_quarter":
            return t("reflections.admin.window.last_quarter");
        case "last_year":
            return t("reflections.admin.window.last_year");
        case "all_time":
            return t("reflections.admin.window.all_time");
    }
}

// The heading and empty state describe the numbers on screen, so they follow the
// applied window rather than the pill while a request is in flight.
const heading = computed(() => headingFor(appliedWindow.value));
const emptyText = computed(() => emptyTextFor(appliedWindow.value));

function headingFor(window: AdminCompletionWindow): string {
    switch (window) {
        case "active":
            return t("reflections.admin.completion.heading.active");
        case "last_30_days":
            return t("reflections.admin.completion.heading.last_30_days");
        case "last_quarter":
            return t("reflections.admin.completion.heading.last_quarter");
        case "last_year":
            return t("reflections.admin.completion.heading.last_year");
        case "all_time":
            return t("reflections.admin.completion.heading.all_time");
    }
}

// Only the Active window invites launching a campaign: a dated window with
// nothing in it is a fact about the past, not a prompt.
function emptyTextFor(window: AdminCompletionWindow): string {
    switch (window) {
        case "active":
            return t("reflections.admin.completion.empty.active");
        case "last_30_days":
            return t("reflections.admin.completion.empty.last_30_days");
        case "last_quarter":
            return t("reflections.admin.completion.empty.last_quarter");
        case "last_year":
            return t("reflections.admin.completion.empty.last_year");
        case "all_time":
            return t("reflections.admin.completion.empty.all_time");
    }
}

function stateLabel(key: AdminCompletionStateFilter): string {
    switch (key) {
        case "all":
            return t("reflections.admin.completion.state.all");
        case "submitted":
            return t("reflections.admin.completion.state.submitted", { count: summary.value?.submitted ?? 0 });
        case "in_progress":
            return t("reflections.admin.completion.state.in_progress", { count: summary.value?.in_progress ?? 0 });
        case "not_started":
            return t("reflections.admin.completion.state.not_started", { count: summary.value?.not_started ?? 0 });
    }
}

const stateOptions = computed(() => COMPLETION_STATES.map((key) => ({ key, label: stateLabel(key) })));

function isMuted(segment: Exclude<AdminCompletionStateFilter, "all">): boolean {
    return state.value !== "all" && state.value !== segment;
}

const downloadLabel = computed(() => {
    if (downloading.value) {
        return t("reflections.admin.completion.preparing");
    }
    if (state.value === "all") {
        return t("reflections.admin.completion.downloadCsv");
    }
    return t("reflections.admin.completion.downloadCsvFiltered", { state: stateLabel(state.value) });
});

// One localized range, which carries the years whenever the window crosses New Year.
const windowLabel = computed(() => {
    const range = formatDateRange(summary.value?.window_start, summary.value?.window_end, "month-day");
    return range || formatDate(summary.value?.window_start, "short") || formatDate(summary.value?.window_end, "short");
});

const sourceLine = computed(() => {
    const current = summary.value;
    if (!current) {
        return "";
    }
    // The total already includes the individual ones, so the breakdown must add up to it.
    const individuals = current.individual_cycle_count;
    const closedAssessments = current.closed_campaign_assessments ?? 0;
    const activeAssessments = current.assessments_in_flight - closedAssessments - individuals;
    if (current.closed_campaign_count === 0) {
        const values = { assessments: current.assessments_in_flight, campaignAssessments: activeAssessments, campaigns: current.active_campaign_count, individuals };
        // "assessment" rather than "360": an individual cycle can be a self-reflection.
        return current.active_campaign_count > 0 ? t("reflections.admin.completion.source", values) : t("reflections.admin.completion.sourceIndividualOnly", values);
    }
    // A dated window: the closed campaigns get their own share of the total.
    return [
        t("reflections.admin.completion.sourceTotal", { count: current.assessments_in_flight }),
        current.active_campaign_count > 0 ? t("reflections.admin.completion.sourceActive", { count: activeAssessments, campaigns: current.active_campaign_count }) : null,
        t("reflections.admin.completion.sourceClosed", { count: closedAssessments, campaigns: current.closed_campaign_count }),
        t("reflections.admin.completion.sourceIndividuals", { count: individuals }),
    ]
        .filter(Boolean)
        .join(" · ");
});

const raterResponseLine = computed(() => {
    const current = summary.value;
    if (!current || current.raters_responded_pct === null || current.raters_responded_pct === undefined) {
        return t("reflections.admin.completion.hiddenByOrgSetting");
    }
    return t("reflections.admin.completion.responseRate", { pct: current.raters_responded_pct });
});

const noteLine = computed(() => {
    const current = summary.value;
    if (!current) {
        return "";
    }
    const closesAt = formatDate(current.earliest_close_at, "short");
    const days = daysFromToday(current.earliest_close_at);
    if (!closesAt || days === null || days < 0) {
        return t("reflections.admin.completion.notStartedNote", { count: current.not_started });
    }
    return t("reflections.admin.completion.notStartedClosing", { count: current.not_started, date: closesAt, days });
});

function widthOf(count: number): string {
    const assessments = summary.value?.assessments_in_flight ?? 0;
    // Clamped for the same reason the backend clamps its percentages: a cycle can
    // outlive its subject's eligibility, so a segment can exceed the whole.
    return assessments ? `${Math.min(100, (count / assessments) * 100)}%` : "0%";
}

async function onDownloadCsv() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_COMPLETION_CSV_DOWNLOAD_CLICKED, { window: timeWindow.value, state: state.value });
    downloading.value = true;
    downloadError.value = null;
    try {
        // The same window and state as the card, so the file matches what the admin is looking at.
        const params = new URLSearchParams({ window: timeWindow.value, state: state.value });
        const downloaded = await downloadApiFile(`/v1/reflections/admin/completion/export-csv?${params}`, "assessment_completion.csv");
        if (!downloaded) {
            downloadError.value = t("reflections.admin.completion.downloadFailed");
        }
    } finally {
        downloading.value = false;
    }
}
</script>

<style scoped>
.completion-layout {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 40px;
}

.completion-bar {
    display: flex;
    width: 100%;
    height: 10px;
    border-radius: 999px;
    overflow: hidden;
    background: var(--color-progress-track);
}

.completion-fill {
    height: 100%;
    transition:
        width 0.3s,
        opacity 0.2s;
}

.fill-submitted {
    background: var(--color-accent);
}

.fill-in-progress {
    background: var(--color-status-warning);
}

.swatch {
    width: 8px;
    height: 8px;
    border-radius: 2px;
}

.swatch-submitted {
    background: var(--color-accent);
}

.swatch-in-progress {
    background: var(--color-status-warning);
}

.swatch-not-started {
    background: var(--color-progress-track);
}

.completion-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-self: start;
}

.completion-tile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 16px;
    border-radius: 10px;
    background: var(--color-surface-sunken);
}
</style>
